import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Modal } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { enqueueSnackbar } from 'notistack';
import {
  getResultsReportDownloadUrl,
  listResultsReportsForDatabase,
  ResultsReportJobStatus,
  ResultsReportRow,
  startResultsReport,
} from 'api/bitedb/resultsReport';
import { useResultsReportJob } from 'hooks/useResultsReportJob';
import {
  IEntityTicketReportData,
  screeningTicketEntityPDF,
} from 'components/Common/EntityTicketPdf';

interface EntityResultsDownloadModalProps {
  open: boolean;
  onClose: () => void;
  databaseId?: string;
  databaseName?: string;
  entityId?: string;
  entityName?: string;
  groupId?: string;
  canAccessVerificationResults?: boolean;
  hasVerificationResults: boolean;
  onViewReports: () => void;
  /**
   * Builds the data for the canonical entity screening report — the same
   * `screeningTicketEntityPDF` format the Entity Screening tool produces when a
   * saved-work ticket is saved. Supplied only on a single entity's details page;
   * when absent (e.g. a whole-database export) that option is not offered and
   * the database report is unchanged. A builder rather than a value so the docs
   * are collected at click time, not on every render.
   */
  buildEntityScreeningReport?: () => IEntityTicketReportData | null;
}

type ModalMode = 'config' | 'running' | 'done' | 'error';

type SelectedJob = {
  key: 'csv' | 'pdf';
  label: string;
  description: string;
  jobId: string | null;
  status: ReturnType<typeof useResultsReportJob>['status'];
  step: ReturnType<typeof useResultsReportJob>['step'];
  processedEntities: ReturnType<typeof useResultsReportJob>['processedEntities'];
  totalEntities: ReturnType<typeof useResultsReportJob>['totalEntities'];
  percent: ReturnType<typeof useResultsReportJob>['percent'];
  error: ReturnType<typeof useResultsReportJob>['error'];
  result: ReturnType<typeof useResultsReportJob>['result'];
};

type ReportJobFallbackMap = Record<string, ResultsReportRow>;

const closeIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
  >
    <path
      d='M18 6L6 18M6 6L18 18'
      stroke='#85888E'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const sanitizeFileStem = (value?: string) => {
  const fallback = 'results_export';
  const baseValue = value?.trim() || fallback;
  const sanitized = baseValue
    .replace(/[^a-zA-Z0-9-_ ]+/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 80);
  return sanitized || fallback;
};

const EntityResultsDownloadModal: React.FC<EntityResultsDownloadModalProps> = ({
  open,
  onClose,
  databaseId,
  databaseName,
  entityId,
  entityName,
  groupId,
  canAccessVerificationResults = true,
  hasVerificationResults,
  onViewReports,
  buildEntityScreeningReport,
}) => {
  const canShowVerificationOption =
    canAccessVerificationResults && hasVerificationResults;
  // Only a single entity has a screening report to render; the database-level
  // export keeps its existing server-generated outputs.
  const canShowScreeningReportOption = Boolean(
    entityId && buildEntityScreeningReport,
  );
  const [mode, setMode] = useState<ModalMode>('config');
  const [includeScreeningCsv, setIncludeScreeningCsv] = useState(true);
  const [includeScreeningReportPdf, setIncludeScreeningReportPdf] = useState(
    canShowScreeningReportOption,
  );
  const [includeVerificationPdf, setIncludeVerificationPdf] = useState(
    canShowVerificationOption,
  );
  const [csvJobId, setCsvJobId] = useState<string | null>(null);
  const [pdfJobId, setPdfJobId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [fallbackRowsByJobId, setFallbackRowsByJobId] =
    useState<ReportJobFallbackMap>({});

  const csvJob = useResultsReportJob(csvJobId);
  const pdfJob = useResultsReportJob(pdfJobId);

  useEffect(() => {
    if (
      !open ||
      mode !== 'running' ||
      !databaseId ||
      !groupId ||
      (!csvJobId && !pdfJobId)
    ) {
      return;
    }

    let isActive = true;
    const trackedJobIds = [csvJobId, pdfJobId].filter(
      (jobId): jobId is string => Boolean(jobId),
    );

    const pollRows = async () => {
      try {
        const response = await listResultsReportsForDatabase(databaseId, groupId, {
          page: 1,
          limit: 25,
        });
        if (!isActive) {
          return;
        }

        const matchedRows = response.data.filter(
          (row) => row.jobId && trackedJobIds.includes(row.jobId),
        );

        if (matchedRows.length === 0) {
          return;
        }

        setFallbackRowsByJobId((current) => {
          const next = { ...current };
          matchedRows.forEach((row) => {
            next[row.jobId] = row;
          });
          return next;
        });
      } catch {
        /* fallback stays silent */
      }
    };

    pollRows();
    const intervalId = window.setInterval(pollRows, 3000);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [csvJobId, databaseId, groupId, mode, open, pdfJobId]);

  const resolveTerminalStatus = useCallback(
    (
      liveStatus: ReturnType<typeof useResultsReportJob>['status'],
      fallbackRow?: ResultsReportRow,
    ): ResultsReportJobStatus | null => {
      if (fallbackRow?.status === 'completed' || fallbackRow?.status === 'failed') {
        return fallbackRow.status;
      }
      return liveStatus;
    },
    [],
  );

  const resolveTerminalStep = useCallback(
    (
      liveStep: ReturnType<typeof useResultsReportJob>['step'],
      fallbackRow?: ResultsReportRow,
    ) => {
      if (fallbackRow?.status === 'completed') {
        return 'completed' as const;
      }
      return liveStep;
    },
    [],
  );

  const resolveTerminalResult = useCallback(
    (
      liveResult: ReturnType<typeof useResultsReportJob>['result'],
      fallbackRow?: ResultsReportRow,
    ) => {
      if (liveResult) {
        return liveResult;
      }
      if (fallbackRow?.status !== 'completed') {
        return null;
      }
      return {
        s3Key: '',
        fileName: fallbackRow.fileName || '',
        entityCount: fallbackRow.entityCount || 0,
        fileSizeBytes: fallbackRow.fileSizeBytes || 0,
        generatedAt: fallbackRow.generatedAt || new Date().toISOString(),
      };
    },
    [],
  );

  useEffect(() => {
    if (open) {
      return;
    }
    setIsStarting(false);
    setIsDownloading(false);
    if (!csvJobId && !pdfJobId) {
      setMode('config');
      setStartError(null);
      setDownloadError(null);
      setFallbackRowsByJobId({});
      setIncludeScreeningCsv(true);
      setIncludeVerificationPdf(canShowVerificationOption);
      setIncludeScreeningReportPdf(canShowScreeningReportOption);
    }
  }, [
    canShowScreeningReportOption,
    canShowVerificationOption,
    csvJobId,
    open,
    pdfJobId,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!canShowVerificationOption) {
      setIncludeVerificationPdf(false);
    }
    if (!canShowScreeningReportOption) {
      setIncludeScreeningReportPdf(false);
    }
  }, [canShowScreeningReportOption, canShowVerificationOption, open]);

  const selectedJobs = useMemo<SelectedJob[]>(
    () =>
      [
        includeScreeningCsv
          ? {
              key: 'csv' as const,
              label: 'Screening results export',
              description: 'CSV of the screening results for this entity',
              jobId: csvJobId,
              status: resolveTerminalStatus(
                csvJob.status,
                csvJobId ? fallbackRowsByJobId[csvJobId] : undefined,
              ),
              step: resolveTerminalStep(
                csvJob.step,
                csvJobId ? fallbackRowsByJobId[csvJobId] : undefined,
              ),
              processedEntities: csvJob.processedEntities,
              totalEntities: csvJob.totalEntities,
              percent: csvJob.percent,
              error:
                csvJob.error ||
                (csvJobId ? fallbackRowsByJobId[csvJobId]?.errorMessage : null) ||
                null,
              result: resolveTerminalResult(
                csvJob.result,
                csvJobId ? fallbackRowsByJobId[csvJobId] : undefined,
              ),
            }
          : null,
        includeVerificationPdf && canShowVerificationOption
          ? {
              key: 'pdf' as const,
              label: 'Verification dossier',
              description: 'A PDF summarising all business verification findings for this entity.',
              jobId: pdfJobId,
              status: resolveTerminalStatus(
                pdfJob.status,
                pdfJobId ? fallbackRowsByJobId[pdfJobId] : undefined,
              ),
              step: resolveTerminalStep(
                pdfJob.step,
                pdfJobId ? fallbackRowsByJobId[pdfJobId] : undefined,
              ),
              processedEntities: pdfJob.processedEntities,
              totalEntities: pdfJob.totalEntities,
              percent: pdfJob.percent,
              error:
                pdfJob.error ||
                (pdfJobId ? fallbackRowsByJobId[pdfJobId]?.errorMessage : null) ||
                null,
              result: resolveTerminalResult(
                pdfJob.result,
                pdfJobId ? fallbackRowsByJobId[pdfJobId] : undefined,
              ),
            }
          : null,
      ].filter((job): job is SelectedJob => Boolean(job)),
    [
      csvJob.error,
      csvJob.percent,
      csvJob.processedEntities,
      csvJob.result,
      csvJob.status,
      csvJob.step,
      csvJob.totalEntities,
      csvJobId,
      canShowVerificationOption,
      fallbackRowsByJobId,
      includeScreeningCsv,
      includeVerificationPdf,
      pdfJob.error,
      pdfJob.percent,
      pdfJob.processedEntities,
      pdfJob.result,
      pdfJob.status,
      pdfJob.step,
      pdfJob.totalEntities,
      pdfJobId,
      resolveTerminalResult,
      resolveTerminalStatus,
      resolveTerminalStep,
    ],
  );

  useEffect(() => {
    if (mode !== 'running' || selectedJobs.length === 0) {
      return;
    }

    const allFinished = selectedJobs.every(
      (job) => job.status === 'completed' || job.status === 'failed',
    );
    if (!allFinished) {
      return;
    }

    const hasCompletedJob = selectedJobs.some((job) => job.status === 'completed');
    setMode(hasCompletedJob ? 'done' : 'error');
  }, [mode, selectedJobs]);

  const buildEntityFilter = useMemo(
    () =>
      entityId
        ? {
            entityFilters: {
              entityIds: [entityId],
            },
          }
        : {},
    [entityId],
  );

  const buildBaseName = useCallback(() => {
    const preferredName = entityName || databaseName || 'results_export';
    return sanitizeFileStem(preferredName);
  }, [databaseName, entityName]);

  const handleGenerate = useCallback(async () => {
    setStartError(null);

    if (!databaseId) {
      setStartError('Database ID is missing. Unable to prepare the export.');
      return;
    }
    if (!groupId) {
      setStartError('No team selected. Choose a team before preparing the export.');
      return;
    }
    if (
      !includeScreeningCsv &&
      !includeVerificationPdf &&
      !includeScreeningReportPdf
    ) {
      setStartError('Select at least one download option to continue.');
      return;
    }

    setIsStarting(true);
    try {
      const baseName = buildBaseName();
      const requests: Promise<void>[] = [];

      // The screening report is rendered in the browser by PDFKit rather than by
      // a server job, so it downloads inline here and never enters the
      // running/done job flow below.
      if (includeScreeningReportPdf && canShowScreeningReportOption) {
        const reportData = buildEntityScreeningReport?.() ?? null;
        if (!reportData) {
          setStartError(
            'Screening results for this entity are still loading. Try again in a moment.',
          );
          setIsStarting(false);
          return;
        }
        await screeningTicketEntityPDF(
          reportData,
          `${baseName}_entity_screening_report.pdf`,
        );
      }

      if (includeScreeningCsv) {
        requests.push(
          startResultsReport({
            databaseId: databaseId,
            groupId: groupId,
            options: {
              reportType: 'hits_only',
              includeExecutiveSummary: false,
              includeScreeningResults: true,
              includeBusinessVerification: false,
              includeFullEvidence: true,
              topNResultsPerEntity: 0,
              output: {
                format: 'csv',
                paperSize: 'LETTER',
                orientation: 'landscape',
              },
              fileName: `${baseName}_screening_results.csv`,
              ...buildEntityFilter,
            },
          }).then((response) => {
            setCsvJobId(response.jobId);
          }),
        );
      }

      if (includeVerificationPdf && canShowVerificationOption) {
        requests.push(
          startResultsReport({
            databaseId: databaseId,
            groupId: groupId,
            options: {
              reportType: 'full',
              includeExecutiveSummary: false,
              includeScreeningResults: false,
              includeBusinessVerification: true,
              includeFullEvidence: true,
              topNResultsPerEntity: 0,
              output: {
                format: 'pdf',
                paperSize: 'LETTER',
                orientation: 'landscape',
              },
              fileName: `${baseName}_verification_report.pdf`,
              ...buildEntityFilter,
            },
          }).then((response) => {
            setPdfJobId(response.jobId);
          }),
        );
      }

      // Nothing server-side was requested: the screening report has already
      // downloaded, so close instead of showing an empty job-progress view.
      if (requests.length === 0) {
        enqueueSnackbar('Entity screening report downloaded.', {
          variant: 'success',
        });
        onClose();
        return;
      }

      await Promise.all(requests);
      setMode('running');
    } catch (error: any) {
      setStartError(error?.message || 'Failed to start the selected downloads.');
    } finally {
      setIsStarting(false);
    }
  }, [
    buildBaseName,
    buildEntityFilter,
    buildEntityScreeningReport,
    canShowScreeningReportOption,
    databaseId,
    groupId,
    onClose,
    canShowVerificationOption,
    includeScreeningCsv,
    includeScreeningReportPdf,
    includeVerificationPdf,
  ]);

  const handleDownloadFiles = useCallback(async () => {
    if (!groupId) {
      setDownloadError('No team selected. Cannot fetch the download URLs.');
      return;
    }

    const completedJobIds = selectedJobs
      .filter((job) => job.status === 'completed' && job.jobId)
      .map((job) => job.jobId as string);

    if (completedJobIds.length === 0) {
      setDownloadError('No completed files are ready to download yet.');
      return;
    }

    setDownloadError(null);
    setIsDownloading(true);
    try {
      const payloads = await Promise.all(
        completedJobIds.map((jobId) => getResultsReportDownloadUrl(jobId, groupId)),
      );
      payloads.forEach((payload) => {
        if (payload?.url) {
          window.open(payload.url, '_blank', 'noopener,noreferrer');
        }
      });
      enqueueSnackbar('Your files are ready and opening in new tabs.', {
        variant: 'success',
      });
    } catch (error: any) {
      setDownloadError(error?.message || 'Failed to prepare the downloads.');
    } finally {
      setIsDownloading(false);
    }
  }, [groupId, selectedJobs]);

  const title = useMemo(() => {
    switch (mode) {
      case 'running':
        return 'Preparing your downloads';
      case 'done':
        return 'Downloads ready';
      case 'error':
        return 'Download preparation failed';
      default:
        return 'Prepare results downloads';
    }
  }, [mode]);

  const subtitle = useMemo(() => {
    switch (mode) {
      case 'running':
        return 'Your selected files are being generated in the background.';
      case 'done':
        return 'Your export files are ready to download or review from Generated Reports.';
      case 'error':
        return 'We could not finish preparing the selected files.';
      default:
        return 'Choose which files to prepare for this entity.';
    }
  }, [mode]);

  const renderSelectionRow = (
    id: string,
    checked: boolean,
    onChange: (value: boolean) => void,
    title: string,
    description: string,
    disabled?: boolean,
  ) => (
    <label
      htmlFor={id}
      style={{
        width: '100%',
        borderRadius: 12,
        border: '1px solid var(--bg-secondary)',
        background: 'var(--bg-primary)',
        padding: 16,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <input
        id={id}
        type='checkbox'
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        style={{ marginTop: 4 }}
      />
      <div style={{ flex: 1 }}>
        <div
          style={{
            color: 'var(--text-primary)',
            fontFamily: 'Inter',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '20px',
          }}
        >
          {title}
        </div>
        <div
          style={{
            color: 'var(--text-secondary)',
            fontFamily: 'Inter',
            fontSize: 13,
            fontWeight: 400,
            lineHeight: '20px',
            marginTop: 4,
          }}
        >
          {description}
        </div>
      </div>
    </label>
  );

  const renderConfigView = () => (
    <div className='audit-add-modal-cont-body' style={{ gap: 16 }}>
      <div className='audit-add-modal-cont-body-frame'>
        {renderSelectionRow(
          'screening-results-export',
          includeScreeningCsv,
          setIncludeScreeningCsv,
          'Screening results export (CSV)',
          'A spreadsheet-friendly export of the screening results for this entity.',
        )}
      </div>

      {canShowScreeningReportOption && (
        <div className='audit-add-modal-cont-body-frame'>
          {renderSelectionRow(
            'entity-screening-report-export',
            includeScreeningReportPdf,
            setIncludeScreeningReportPdf,
            'Entity screening report (PDF)',
            'The full screening report for this entity, in the same format as the Entity Screening tool’s saved-work report.',
          )}
        </div>
      )}

      {canAccessVerificationResults && (
        <div className='audit-add-modal-cont-body-frame'>
          {renderSelectionRow(
            'verification-report-export',
            includeVerificationPdf,
            setIncludeVerificationPdf,
            'Verification dossier (PDF)',
            hasVerificationResults
              ? 'A PDF summarising all business verification findings for this entity.'
              : 'Verification results are not available for this entity yet.',
            !hasVerificationResults,
          )}
        </div>
      )}

      {startError && (
        <div className='audit-add-modal-cont-body-frame'>
          <div
            style={{
              width: '100%',
              borderRadius: 12,
              border: '1px solid #FECACA',
              background: '#FEF2F2',
              padding: '12px 14px',
              color: '#B42318',
              fontFamily: 'Inter',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: '20px',
            }}
          >
            {startError}
          </div>
        </div>
      )}
    </div>
  );

  const renderJobCompactCard = (job: SelectedJob) => {
    const isFailed = job.status === 'failed';
    const isComplete = job.status === 'completed';
    const progressValue =
      typeof job.percent === 'number' && Number.isFinite(job.percent)
        ? Math.max(0, Math.min(100, job.percent))
        : null;
    const barColor = isFailed ? '#D92D20' : isComplete ? '#12B76A' : 'var(--action-color)';
    const statusLabel = isComplete ? 'Ready' : isFailed ? 'Failed' : 'Working';

    return (
      <div
        key={job.key}
        style={{
          flex: '1 1 0',
          minWidth: 180,
          borderRadius: 12,
          border: '1px solid var(--bg-secondary)',
          background: 'var(--bg-primary)',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ color: 'var(--text-primary)', fontFamily: 'Inter', fontSize: 13, fontWeight: 600, lineHeight: '18px' }}>
            {job.label}
          </div>
          <div style={{ color: isFailed ? '#B42318' : isComplete ? '#12B76A' : 'var(--text-secondary)', fontFamily: 'Inter', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
            {statusLabel}
          </div>
        </div>
        <div style={{ width: '100%', height: 6, borderRadius: 999, background: 'var(--bg-secondary)', overflow: 'hidden' }}>
          <div
            style={{
              width: `${progressValue ?? 20}%`,
              height: '100%',
              borderRadius: 999,
              background: barColor,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        {job.step && !isComplete && !isFailed && (
          <div style={{ color: 'var(--text-secondary)', fontFamily: 'Inter', fontSize: 12, fontWeight: 400, lineHeight: '16px' }}>
            {job.step.replace(/_/g, ' ')}…
          </div>
        )}
        {isFailed && job.error && (
          <div style={{ color: '#B42318', fontFamily: 'Inter', fontSize: 12, fontWeight: 400, lineHeight: '16px' }}>
            {job.error}
          </div>
        )}
      </div>
    );
  };

  const renderRunningView = () => {
    const isMulti = selectedJobs.length > 1;

    if (isMulti) {
      return (
        <div className='audit-add-modal-cont-body' style={{ gap: 16 }}>
          <div className='audit-add-modal-cont-body-frame'>
            <div style={{ display: 'flex', gap: 12, width: '100%', flexWrap: 'wrap' }}>
              {selectedJobs.map((job) => renderJobCompactCard(job))}
            </div>
          </div>
        </div>
      );
    }

    const job = selectedJobs[0];
    if (!job) return null;

    const progressValue =
      typeof job.percent === 'number' && Number.isFinite(job.percent)
        ? Math.max(0, Math.min(100, job.percent))
        : null;
    const isFailed = job.status === 'failed';
    const barColor = isFailed ? '#D92D20' : 'var(--action-color)';
    const stepLabel = job.step
      ? job.step.replace(/_/g, ' ')
      : 'Working';

    return (
      <div className='audit-add-modal-cont-body' style={{ gap: 0 }}>
        <div className='audit-add-modal-cont-body-frame'>
          <div
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '28px 16px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
            }}
          >
            {!isFailed && (
              <CircularProgress size={36} sx={{ color: 'var(--action-color)' }} />
            )}
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: 'Inter',
                  fontSize: 14,
                  fontWeight: 600,
                  color: isFailed ? '#B42318' : 'var(--text-primary)',
                  lineHeight: '20px',
                }}
              >
                {job.label}
              </div>
              <div
                style={{
                  fontFamily: 'Inter',
                  fontSize: 13,
                  color: isFailed ? '#B42318' : 'var(--text-secondary)',
                  lineHeight: '20px',
                  marginTop: 4,
                  textTransform: 'capitalize',
                }}
              >
                {isFailed && job.error ? job.error : stepLabel}
              </div>
            </div>
            <div
              style={{
                width: '100%',
                height: 6,
                borderRadius: 999,
                background: 'var(--bg-secondary)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progressValue ?? 20}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: barColor,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            {typeof job.processedEntities === 'number' &&
              typeof job.totalEntities === 'number' &&
              job.totalEntities > 0 && (
                <div
                  style={{
                    fontFamily: 'Inter',
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    lineHeight: '16px',
                  }}
                >
                  {job.processedEntities} of {job.totalEntities} entities processed
                </div>
              )}
          </div>
        </div>
      </div>
    );
  };

  const renderDoneView = () => (
    <div className='audit-add-modal-cont-body' style={{ gap: 16 }}>
      {selectedJobs.map((job) => {
        const isCompleted = job.status === 'completed';
        return (
          <div className='audit-add-modal-cont-body-frame' key={job.key}>
            <div
              style={{
                width: '100%',
                borderRadius: 12,
                border: `1px solid ${isCompleted ? '#B7E4C7' : '#FECACA'}`,
                background: isCompleted ? '#F0FDF4' : '#FEF2F2',
                padding: '16px',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  color: isCompleted ? '#166534' : '#B42318',
                  fontSize: 22,
                  marginTop: '2px',
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    color: 'var(--bg-primary)',
                    fontFamily: 'Inter',
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: '20px',
                  }}
                >
                  {job.label}
                </div>
                <div
                  style={{
                    color: 'var(--bg-tertiary)',
                    fontFamily: 'Inter',
                    fontSize: 13,
                    fontWeight: 400,
                    lineHeight: '20px',
                    marginTop: 4,
                  }}
                >
                  {isCompleted
                    ? job.result?.fileName || job.description
                    : job.error || 'This file could not be generated.'}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {downloadError && (
        <div className='audit-add-modal-cont-body-frame'>
          <div
            style={{
              width: '100%',
              borderRadius: 12,
              border: '1px solid #FECACA',
              background: '#FEF2F2',
              padding: '12px 14px',
              color: '#B42318',
              fontFamily: 'Inter',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: '20px',
            }}
          >
            {downloadError}
          </div>
        </div>
      )}
    </div>
  );

  const renderErrorView = () => (
    <div className='audit-add-modal-cont-body' style={{ gap: 16 }}>
      <div className='audit-add-modal-cont-body-frame'>
        <div
          style={{
            width: '100%',
            borderRadius: 12,
            border: '1px solid #FECACA',
            background: '#FEF2F2',
            padding: '16px',
          }}
        >
          <div
            style={{
              color: '#B42318',
              fontFamily: 'Inter',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '20px',
            }}
          >
            Download preparation failed
          </div>
          <div
            style={{
              color: '#B42318',
              fontFamily: 'Inter',
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '20px',
              marginTop: 4,
              whiteSpace: 'pre-wrap',
            }}
          >
            {selectedJobs.find((job) => job.error)?.error ||
              startError ||
              'We could not prepare the selected files.'}
          </div>
        </div>
      </div>
    </div>
  );

  const renderFooter = () => {
    if (mode === 'config') {
      return (
        <div className='audit-modal-cont-footer'>
          <div className='audit-modal-cont-footer-content'>
            <div className='audit-modal-cont-footer-content-btn-cancel' onClick={onClose}>
              <div className='audit-modal-cont-footer-content-btn-text'>Cancel</div>
            </div>
            <div
              className='audit-modal-cont-footer-content-btn-upload'
              onClick={isStarting ? undefined : handleGenerate}
              style={isStarting ? { opacity: 0.75, cursor: 'wait' } : undefined}
            >
              <div className='audit-modal-cont-footer-content-btn-text' style={{ color: '#FFF' }}>
                {isStarting && (
                  <CircularProgress size={18} sx={{ color: '#FFF', marginRight: '10px' }} />
                )}
                {isStarting ? 'Starting...' : 'Prepare Downloads'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (mode === 'running') {
      return (
        <div className='audit-modal-cont-footer'>
          <div className='audit-modal-cont-footer-content'>
            <div className='audit-modal-cont-footer-content-btn-cancel' onClick={onClose}>
              <div className='audit-modal-cont-footer-content-btn-text'>Close</div>
            </div>
            <div className='audit-modal-cont-footer-content-btn-upload' onClick={onViewReports}>
              <div className='audit-modal-cont-footer-content-btn-text' style={{ color: '#FFF' }}>
                View Reports
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (mode === 'done') {
      return (
        <div className='audit-modal-cont-footer'>
          <div className='audit-modal-cont-footer-content'>
            <div className='audit-modal-cont-footer-content-btn-cancel' onClick={onClose}>
              <div className='audit-modal-cont-footer-content-btn-text'>Close</div>
            </div>
            <div className='audit-modal-cont-footer-content-btn-upload' onClick={onViewReports}>
              <div className='audit-modal-cont-footer-content-btn-text' style={{ color: '#FFF' }}>
                View Reports
              </div>
            </div>
            <div
              className='audit-modal-cont-footer-content-btn-upload'
              onClick={isDownloading ? undefined : handleDownloadFiles}
              style={{ flex: '1.5 0 0', ...(isDownloading ? { opacity: 0.75, cursor: 'wait' } : {}) }}
            >
              <div className='audit-modal-cont-footer-content-btn-text' style={{ color: '#FFF', whiteSpace: 'nowrap' }}>
                {isDownloading ? (
                  <CircularProgress size={18} sx={{ color: '#FFF', marginRight: '10px' }} />
                ) : (
                  <DownloadOutlinedIcon sx={{ fontSize: 18, marginRight: '8px' }} />
                )}
                {isDownloading ? 'Preparing...' : 'Download Files'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='audit-modal-cont-footer'>
        <div className='audit-modal-cont-footer-content'>
          <div className='audit-modal-cont-footer-content-btn-cancel' onClick={onClose}>
            <div className='audit-modal-cont-footer-content-btn-text'>Close</div>
          </div>
          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={() => {
              setMode('config');
              setStartError(null);
              setDownloadError(null);
              setCsvJobId(null);
              setPdfJobId(null);
            }}
          >
            <div className='audit-modal-cont-footer-content-btn-text' style={{ color: '#FFF' }}>
              Try Again
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ outline: 'none' }}>
        <div
          className='audit-add-modal-cont'
          style={{
            width: 560,
            maxWidth: 'calc(100vw - 32px)',
            alignItems: 'stretch',
          }}
        >
          <div className='audit-add-modal-cont-head'>
            <div className='audit-add-modal-head-cont-change'>
              <div className='audit-add-modal-head-cont-icon'>
                {mode === 'done' ? (
                  <CheckCircleOutlineIcon
                    sx={{ color: '#4895EF', width: '24px', height: '24px' }}
                  />
                ) : mode === 'config' ? (
                  <DescriptionOutlinedIcon
                    sx={{ color: '#4895EF', width: '24px', height: '24px' }}
                  />
                ) : (
                  <PictureAsPdfOutlinedIcon
                    sx={{ color: '#4895EF', width: '24px', height: '24px' }}
                  />
                )}
              </div>
              <div className='audit-add-modal-head-cont-supporting'>
                <h1>{title}</h1>
                <p>{subtitle}</p>
              </div>
            </div>
            <div className='audit-add-modal-head-close' onClick={onClose}>
              {closeIcon}
            </div>
          </div>

          <div className='activity-cont-frame-left-table-feed-row-cont-divider' />

          {mode === 'config' && renderConfigView()}
          {mode === 'running' && renderRunningView()}
          {mode === 'done' && renderDoneView()}
          {mode === 'error' && renderErrorView()}

          {renderFooter()}
        </div>
      </div>
    </Modal>
  );
};

export default EntityResultsDownloadModal;
